<template>
  <div>
    <div v-if="!isReadOnly" class="my-2">
      <validation-observer
        novalidate
        disabled
        tag="form"
        ref="sectionValidator"
        @submit.prevent="add"
      >
        <validation-provider v-slot="{ errors }" mode="lazy" rules="required">
          <custom-input
            v-model="section.name"
            label="Nombre de la Sección:"
            custom-error-msg="El campo es requerido"
            :error="errors[0]"
          />
        </validation-provider>
      </validation-observer>

      <div class="d-flex flex-row-reverse">
        <action-button
          text="Añadir Sección"
          i="fas fa-file-plus"
          variant="success"
          class="mt-3"
          :disabled="isDragging || !section.name"
          @click="add"
        />
      </div>
    </div>
    <div v-if="model.length">
      <section-title title="Secciones" has-line />
      <div class="my-2 d-flex justify-content-end">
        <action-button
          i="fal fa-sort-alt"
          class="outline-dark-gray mr-2"
          :class="{ 'bg-dark-blue text-white': dragging }"
          :disabled="isReadOnly || model.length < 2"
          @click="reorganize"
        >
          {{ dragging ? 'Finalizar Reorganización' : 'Reorganizar Secciones' }}
        </action-button>
        <action-button
          :i="allExpanded ? 'fal fa-compress-arrows-alt' : 'fal fa-expand-alt'"
          :disabled="isDragging"
          class="outline-dark-gray"
          @click="toggle"
        >
          {{ allExpanded ? 'Colapsar Todo' : 'Expandir Todo' }}
        </action-button>
      </div>
      <div class="mx-3">
        <custom-collapse id="sections-collapse">
          <draggable :list="model" :disabled="!dragging">
            <custom-collapse-item
              v-for="(item, key) in model"
              :id="`section-collapse-item-${key + 1}`"
              :key="`section-collapse-item-${key + 1}`"
              :ref="`section-collapse-item-${key + 1}`"
              :auto-expand-collapse-enabled="false"
              :keep-btn-visible="false"
              :expanded="item.expanded"
              :title="item.name"
              :class="{ invalid: item.valid === false }"
              :disabled="isReadOnly || isDragging"
              :show-icon-when-button-disabled="dragging"
              icon-class-when-button-disabled="fas fa-grip-vertical d-flex flex-fill justify-content-end align-items-center data-label"
              class="my-3 rounded collapse-item section"
              button-icon="far fa-times-circle color-red"
              expanded-icon="fas fa-plus-circle text-dark-blue"
              parent-id="sections-collapse"
              read-only-icon="fas fa-plus-circle color-gray-2"
              @actionButtonClick="quit(item, key)"
              @expandButtonClick="expand(item, key)"
            >
              <template #title-left>
                <span class="title-number-box rounded ml-2 p-1 text-white">
                  {{ key + 1 }}
                </span>
              </template>

              <div class="d-flex justify-content-between">
                <div class="section-title">
                  <div class="d-inline mr-2 bg-white">
                    <i class="fas fa-list text-orange fa-lg" />
                  </div>
                  <span class="color-gray-7 font-16 font-weight-bold">
                    Información de Sección
                  </span>
                </div>
                <action-button
                  i="fal fa-sort-alt"
                  class="outline-dark-gray mr-2"
                  :class="{ 'bg-dark-blue text-white': item.dragging }"
                  :disabled="isReadOnly || !canDragQuestions(item, key)"
                  @click="item.dragging = !item.dragging"
                >
                  {{
                    item.dragging
                      ? 'Finalizar Reorganización'
                      : 'Reorganizar Preguntas'
                  }}
                </action-button>
              </div>

              <questions
                v-model="item.questions"
                :dragging="item.dragging"
                :types="questionTypes"
                :is-read-only="isReadOnly || isDragging"
                :ref="`section-${key}-questions`"
                @add="(q) => addQuestion(q, item)"
                @quit="(q) => quitQuestion(q, item)"
              />
            </custom-collapse-item>
          </draggable>
        </custom-collapse>
      </div>
    </div>
    <custom-result-not-found
      v-else
      class="mt-4"
      text="No hay secciones agregadas"
    />
  </div>
</template>

<script>
import alertMixin from '@/mixin/alert.mixin.js';

import { ValidationObserver, ValidationProvider } from 'vee-validate';
import SectionTitle from 'SectionTitle';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomInput from 'CustomInput';
import CustomResultNotFound from 'custom-result-not-found';
import Draggable from 'vuedraggable';

import CustomCollapse from '@/components/common/CustomCollapse.vue';
import CustomCollapseItem from '@/components/common/CustomCollapseItem.vue';

import Questions from './Questions';

export default {
  name: 'QuestionnaireSections',
  components: {
    ValidationObserver,
    ValidationProvider,
    SectionTitle,
    CustomDropDown,
    ActionButton,
    CustomInput,
    CustomResultNotFound,
    Draggable,
    CustomCollapse,
    CustomCollapseItem,
    Questions,
  },
  mixins: [alertMixin],
  props: {
    value: {
      type: Array,
      required: true,
    },
    questionTypes: {
      type: Array,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      section: {
        name: null,
      },
      dragging: false,
    };
  },
  computed: {
    model: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    allExpanded() {
      return this.model.every((x) => x.expanded);
    },
    isDragging() {
      return this.dragging || this.model.some((x) => x.dragging);
    },
    canDragQuestions() {
      return (item, key) => {
        const isOtherDragging = this.model
          .filter((_, i) => i !== key)
          .some((x) => x.dragging);

        return !this.dragging && item.questions?.length > 1 && !isOtherDragging;
      };
    },
  },
  methods: {
    resetSectionForm() {
      this.section.name = null;
      this.$refs.sectionValidator.reset();
    },
    expand(item, key) {
      this.model
        .filter((_, i) => i !== key)
        .forEach((x) => (x.expanded = false));
      setTimeout(() => (item.expanded = !item.expanded), 100);
    },
    toggle() {
      const expanded = !this.allExpanded;
      this.model.forEach((x) => (x.expanded = expanded));
    },
    reorganize() {
      this.dragging = !this.dragging;
    },
    async add() {
      const canAdd = await this.$refs.sectionValidator.validate();
      if (!canAdd) {
        this.ShowToast(
          'Alerta',
          'Favor completar la información solicitada.',
          'error'
        );
        return;
      }
      const valid = await this.validate();
      if (!valid) {
        this.ShowToast(
          'Alerta',
          'Es necesario documentar las secciones agregadas.',
          'error'
        );
        return;
      }
      const item = {
        ...this.section,
        valid: true,
        expanded: false,
        dragging: false,
        questions: [],
      };

      this.model.push(item);
      this.resetSectionForm();
      this.expand(item, this.model.length - 1);
      this.$emit('add', item);
      this.$emit('add:section', item);
    },
    async quit(item, key) {
      const confirmed = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea remover la sección <strong>${item.name}</strong>, se eliminarán los datos suministrados hasta ahora?`,
        'Si',
        'No'
      );

      if (!confirmed) return;
      this.model.splice(key, 1);
      this.$emit('quit', item);
      this.$emit('quit:section', item);
    },
    async validate() {
      if (!this.model.length) return true;
      const forms = this.model.map((item, key) => {
        const validator = this.$refs[`section-${key}-questions`][0];
        return validator.validate().then((val) => (item.valid = val));
      });
      await Promise.all(forms);
      return this.model.every((x) => x.valid);
    },
    addQuestion(item, section) {
      this.$emit('add:question', item, section);
    },
    quitQuestion(item, section) {
      this.$emit('quit:question', item, section);
    },
  },
};
</script>

<style scoped lang="scss">
.outline-dark-gray {
  color: #3f5b60;
  border-color: #3f5b60;
}
.collapse-item ::v-deep .text-dark-blue {
  color: #405c64;
}
.collapse-item ::v-deep .card-header {
  padding: 0.75rem 1rem !important;
}

.collapse-item ::v-deep .tag-badge-pei {
  background: none;
}

.collapse-item.section ::v-deep .shadow2 {
  -webkit-box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
  box-shadow: none !important;
  background-color: #f1f6f4;
}
.collapse-item.section ::v-deep .card-header {
  border-radius: 0.25rem !important;
  border-bottom: 2px solid #e6e6e6;
  background-color: #f1f6f4;
}
.collapse-item.section ::v-deep .card-header .title-number-box {
  background-color: #2db782;
}

.collapse-item.invalid.section {
  border: 1px solid #ff0018 !important;
}
.collapse-item.invalid.section ::v-deep .card-header .title-number-box {
  background-color: #ff5b5c !important;
}
.bg-dark-blue {
  background-color: #405c64;
}
.section-title > div {
  border: 3px solid white;
  padding: 6px 7px;
  border-radius: 30px;
  -webkit-box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
  box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.1607843137);
}
</style>
